/**
 * What makes a run of pull requests one stack, when no host has said so.
 *
 * A pull request whose base branch is another's head branch is built on it. Following that
 * relation bottom to top gives the order a reader should review in. The rule is deliberately
 * conservative: an ambiguous parent or a cycle leaves its members unchained rather than
 * inventing an order, because a wrong order is worse than no order.
 *
 * It is stated once here because two surfaces need it — a thread's linked collection, and a
 * workspace listing, where it costs nothing because the rows already carry both branch names.
 */

export interface PullRequestChainInput<T> {
  /** Stable identity, unique across the items being chained. */
  readonly key: (item: T) => string;
  /** Scopes the branch names, so two repositories cannot chain into each other. */
  readonly scope: (item: T) => string;
  /** Null where the branch is not known, which leaves the item unchained. */
  readonly headBranch: (item: T) => string | null;
  readonly baseBranch: (item: T) => string | null;
}

export function chainPullRequestsByBaseBranch<T>(
  items: ReadonlyArray<T>,
  input: PullRequestChainInput<T>,
): ReadonlyArray<ReadonlyArray<T>> {
  const branchKey = (item: T, branch: string) => `${input.scope(item)}:${branch}`;

  // A head branch used by two items cannot identify a parent, so it identifies none.
  const byHead = new Map<string, T | null>();
  for (const item of items) {
    const head = input.headBranch(item);
    if (head === null) continue;
    const key = branchKey(item, head);
    byHead.set(key, byHead.has(key) ? null : item);
  }

  const parentOf = (item: T): T | undefined => {
    const base = input.baseBranch(item);
    if (base === null) return undefined;
    const parent = byHead.get(branchKey(item, base));
    return parent == null || input.key(parent) === input.key(item) ? undefined : parent;
  };

  const hasChild = new Set<string>();
  for (const item of items) {
    const parent = parentOf(item);
    if (parent !== undefined) hasChild.add(input.key(parent));
  }

  const placed = new Set<string>();
  const chains: Array<ReadonlyArray<T>> = [];
  // Walk down from each top — an item nothing else is built on — collecting bottom to top.
  for (const top of items) {
    if (hasChild.has(input.key(top))) continue;
    const layers: Array<T> = [];
    let cursor: T | undefined = top;
    while (cursor !== undefined && !placed.has(input.key(cursor))) {
      placed.add(input.key(cursor));
      layers.unshift(cursor);
      cursor = parentOf(cursor);
    }
    if (layers.length > 0) chains.push(layers);
  }
  // A cycle has no top. Keep those visible, each on its own, rather than dropping them.
  for (const item of items) {
    if (!placed.has(input.key(item))) chains.push([item]);
  }
  return chains;
}
